import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import dirigenteService from '../services/dirigenteService';
import type { Dirigente } from '../types/dirigente.types';
import {
  SEXO_OPTIONS,
  ESTADO_CIVIL_OPTIONS,
  CARGO_OPTIONS,
  UF_OPTIONS
} from '../types/dirigente.types';
import DocumentosPessoaisUpload from '../components/DocumentosPessoaisUpload';
import { UI_THEME } from '../ui/theme';
import { notifyError, notifyInfo } from '../utils/notify';
import { toUpperText } from '../utils/formatters';

const DiretoriaPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const instituicaoId = searchParams.get('instituicaoId');

  const [dirigentes, setDirigentes] = useState<Dirigente[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showInativarModal, setShowInativarModal] = useState(false);
  const [showDocumentosModal, setShowDocumentosModal] = useState(false);
  const [editingDirigente, setEditingDirigente] = useState<Dirigente | null>(null);
  const [inativandoDirigente, setInativandoDirigente] = useState<Dirigente | null>(null);
  const [dirigenteDocumentos, setDirigenteDocumentos] = useState<Dirigente | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [apenasAtivos, setApenasAtivos] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [foundDirigente, setFoundDirigente] = useState<Dirigente | null>(null);
  const isSaas = UI_THEME === 'saas';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const headerClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const panelClass = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500';
  const labelClass = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200';
  const primaryButtonClass = isSaas
    ? 'inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-700'
    : 'inline-flex items-center justify-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800';
  const secondaryButtonClass = 'inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const dangerButtonClass = 'inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700';

  const [formData, setFormData] = useState<Dirigente>({
    instituicaoId: instituicaoId || '',
    nomeCompleto: '',
    cpf: '',
    rg: '',
    orgaoExpedidor: '',
    ufOrgaoExpedidor: '',
    dataExpedicao: '',
    dataNascimento: '',
    sexo: '',
    nacionalidade: 'Brasileira',
    estadoCivil: '',
    cargo: '',
    dataInicioCargo: '',
    statusCargo: 'Ativo',
    telefone: '',
    email: '',
    cep: '',
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: ''
  });

  const [inativarData, setInativarData] = useState({
    dataTermino: '',
    motivo: ''
  });

  useEffect(() => {
    if (!instituicaoId) {
      notifyError('ID da instituição não informado');
      navigate('/dashboard');
      return;
    }
    loadDirigentes();
    loadAvisos();
  }, [instituicaoId, apenasAtivos]);

  useEffect(() => {
    // Auto-clear success messages after 4 seconds
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(t);
  }, [successMessage]);

  const loadDirigentes = async () => {
    try {
      setLoading(true);
      const data = await dirigenteService.listar(instituicaoId!, apenasAtivos);
      const list = Array.isArray(data) ? data : [];
      const flattened: Dirigente[] = [];

      list.forEach((d: any) => {
        if (d && (d.cargo || d.dataInicioCargo) && !Array.isArray(d.vinculos)) {
          flattened.push(d);
          return;
        }

        if (d && Array.isArray(d.vinculos) && d.vinculos.length > 0) {
          d.vinculos.forEach((v: any) => {
            const item: Dirigente = {
              id: v.id ?? `${d.id || 'p'}-${v.instituicaoId || ''}-${v.cargo || ''}-${v.dataInicioCargo || ''}`,
              pessoaId: d.id,
              instituicaoId: v.instituicaoId || instituicaoId || '',
              nomeCompleto: d.nomeCompleto || d.nome || '',
              nomeSocial: d.nomeSocial,
              cpf: d.cpf || '',
              rg: d.rg || '',
              orgaoExpedidor: d.orgaoExpedidor || '',
              ufOrgaoExpedidor: d.ufOrgaoExpedidor || '',
              dataExpedicao: d.dataExpedicao || '',
              dataNascimento: d.dataNascimento || '',
              sexo: d.sexo || '',
              nacionalidade: d.nacionalidade || 'Brasileira',
              estadoCivil: d.estadoCivil || '',
              cargo: v.cargo || '',
              dataInicioCargo: v.dataInicioCargo || '',
              dataTerminoCargo: v.dataTerminoCargo,
              statusCargo: v.statusCargo || '',
              motivoInativacao: v.motivoInativacao,
              telefone: d.telefone || '',
              celular: d.celular,
              email: d.email || d.emailInstitucional || '',
              cep: d.cep || '',
              logradouro: d.logradouro || '',
              numero: d.numero || '',
              complemento: d.complemento,
              bairro: d.bairro || '',
              cidade: d.cidade || '',
              uf: d.uf || '',
            };

            if (apenasAtivos && String(item.statusCargo || '').toLowerCase() !== 'ativo') return;
            flattened.push(item);
          });
          return;
        }

        flattened.push(d);
      });

      setDirigentes(flattened);
    } catch (error) {
      console.error('Erro ao carregar dirigentes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvisos = async () => {
    try {
      const response = await dirigenteService.verificarAvisos(instituicaoId!);
      setAvisos(response.avisos);
    } catch (error) {
      console.error('Erro ao carregar avisos:', error);
    }
  };

  const handleOpenModal = (dirigente?: Dirigente) => {
    if (dirigente) {
      setEditingDirigente(dirigente);
      setFormData(dirigente);
    } else {
      setEditingDirigente(null);
      setFormData({
        instituicaoId: instituicaoId || '',
        nomeCompleto: '',
        cpf: '',
        rg: '',
        orgaoExpedidor: '',
        ufOrgaoExpedidor: '',
        dataExpedicao: '',
        dataNascimento: '',
        sexo: '',
        nacionalidade: 'Brasileira',
        estadoCivil: '',
        cargo: '',
        dataInicioCargo: '',
        statusCargo: 'Ativo',
        telefone: '',
        email: '',
        cep: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cidade: '',
        uf: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDirigente(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingDirigente) {
        await dirigenteService.atualizar(editingDirigente.pessoaId || editingDirigente.id!, formData);
        // alert('Dirigente atualizado com sucesso!');
        setSuccessMessage('Dirigente atualizado com sucesso!');
      } else {
        await dirigenteService.criar(formData);
        // alert('Dirigente cadastrado com sucesso!');
        setSuccessMessage('Dirigente cadastrado com sucesso!');
      }

      handleCloseModal();
      loadDirigentes();
      loadAvisos();
    } catch (error: any) {
      console.error('Erro ao salvar dirigente:', error);
      notifyError(error.response?.data?.error || 'Erro ao salvar dirigente');
    }
  };

  const handleOpenInativarModal = (dirigente: Dirigente) => {
    setInativandoDirigente(dirigente);
    setInativarData({ dataTermino: '', motivo: '' });
    setShowInativarModal(true);
  };

  const handleOpenDocumentosModal = (dirigente: Dirigente) => {
    setDirigenteDocumentos(dirigente);
    setShowDocumentosModal(true);
  };

  const handleCloseDocumentosModal = () => {
    setShowDocumentosModal(false);
    setDirigenteDocumentos(null);
  };

  const handleInativar = async () => {
    if (!inativandoDirigente) return;

    if (!inativarData.dataTermino || !inativarData.motivo) {
      notifyError('Data de término e motivo são obrigatórios');
      return;
    }

    try {
      await dirigenteService.inativar(
        inativandoDirigente.pessoaId || inativandoDirigente.id!,
        inativarData.dataTermino,
        inativarData.motivo
      );
      // alert('Dirigente inativado com sucesso!');
      setSuccessMessage('Dirigente inativado com sucesso!');
      setShowInativarModal(false);
      loadDirigentes();
      loadAvisos();
    } catch (error: any) {
      console.error('Erro ao inativar dirigente:', error);
      notifyError(error.response?.data?.error || 'Erro ao inativar dirigente');
    }
  };

  const handleCpfLookup = async (rawCpf?: string) => {
    // Avoid mixing ?? and || (Babel parser error). Use logical OR to pick first truthy value.
    const cpfToLookup = (rawCpf || formData.cpf || '').replace(/\D/g, '');
    if (!cpfToLookup) return;
    try {
      const found = await dirigenteService.buscarPorCpf(cpfToLookup);
      if (!found) {
        setFoundDirigente(null);
        notifyInfo('CPF não encontrado no cadastro. Você pode continuar e cadastrar a pessoa.');
        return;
      }

      setFoundDirigente(found);

      // Autofill a subset of formData with found dirigente if the user wants to edit after linking
      setFormData(prev => ({
        ...prev,
        nomeCompleto: found.nomeCompleto || prev.nomeCompleto,
        cpf: (found.cpf || '').replace(/\D/g, ''),
        email: found.email || prev.email,
        telefone: found.telefone || prev.telefone,
      }));

      setSuccessMessage('Encontramos um dirigente com este CPF. Confirme para vincular à instituição ou edite os dados');
    } catch (err: any) {
      console.error('Erro ao buscar CPF:', err);
      notifyError(err?.response?.data?.error || 'Erro ao buscar por CPF');
    }
  };

  const handleConfirmVinculo = async () => {
    if (!foundDirigente) return;
    try {
      await dirigenteService.criarVinculo(foundDirigente.id!, { instituicaoId: instituicaoId!, cargo: formData.cargo, dataInicioCargo: formData.dataInicioCargo, statusCargo: formData.statusCargo });
      setSuccessMessage('Vínculo criado com sucesso');
      setFoundDirigente(null);
      loadDirigentes();
      loadAvisos();
      setShowModal(false);
    } catch (err: any) {
      console.error('Erro ao confirmar vínculo:', err);
      notifyError(err?.response?.data?.error || 'Erro ao criar vínculo');
    }
  };

  const formatCPF = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatCEP = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  const formatPhone = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  return (
    <div className="w-full space-y-6">
      <div className={`rounded-2xl border p-5 ${headerClass}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.22em] ${accentTextClass}`}>
              Gestão
            </p>
            <h1 className="mt-2 text-2xl font-bold text-slate-950 dark:text-slate-50">Diretoria</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Gerencie os dirigentes vinculados à instituição.</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className={primaryButtonClass}
          >
            + Novo dirigente
          </button>
        </div>

        {avisos.length > 0 && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Atenção: cargos obrigatórios não preenchidos</h3>
                <div className="mt-2 text-sm text-amber-800 dark:text-amber-100">
                  <ul className="list-disc pl-5 space-y-1">
                    {avisos.map((aviso, idx) => (
                      <li key={idx}>{aviso}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-4">
          <label className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950/70 dark:text-slate-200">
            <input
              type="checkbox"
              checked={apenasAtivos}
              onChange={(e) => setApenasAtivos(e.target.checked)}
              className="rounded border-slate-300 text-blue-700 focus:ring-blue-500 dark:border-slate-700"
            />
            <span>Mostrar apenas ativos</span>
          </label>
        </div>
      </div>

      {loading ? (
        <div className={`${panelClass} py-10 text-center`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Carregando dirigentes...</p>
        </div>
      ) : dirigentes.length === 0 ? (
        <div className={`${panelClass} py-10 text-center`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Nenhum dirigente cadastrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {dirigentes.map((dirigente) => (
            <div
              key={dirigente.id}
              className={`${panelClass} p-5 transition-shadow hover:shadow-md`}
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-base font-semibold text-slate-950 dark:text-slate-50">{dirigente.nomeCompleto}</h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{dirigente.cargo}</p>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    dirigente.statusCargo === 'Ativo'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                  }`}
                >
                  {dirigente.statusCargo}
                </span>
              </div>

              <div className="mb-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <div className="flex min-w-0 items-center">
                  <svg className="mr-2 h-4 w-4 flex-shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="truncate">{dirigente.email || 'E-mail não informado'}</span>
                </div>
                <div className="flex items-center">
                  <svg className="mr-2 h-4 w-4 flex-shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {dirigente.telefone || 'Telefone não informado'}
                </div>
                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Início: {new Date(dirigente.dataInicioCargo).toLocaleDateString('pt-BR')}
                  {dirigente.dataTerminoCargo && (
                    <> | Término: {new Date(dirigente.dataTerminoCargo).toLocaleDateString('pt-BR')}</>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleOpenModal(dirigente)}
                  className={primaryButtonClass}
                >
                  Editar
                </button>
                <button
                  onClick={() => handleOpenDocumentosModal(dirigente)}
                  className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
                  title="Gerenciar documentos pessoais"
                >
                  Documentos
                </button>
                {dirigente.statusCargo === 'Ativo' && (
                  <button
                    onClick={() => handleOpenInativarModal(dirigente)}
                    className={secondaryButtonClass}
                  >
                    Inativar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Simplificado de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4">
          <div className="my-8 w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <form onSubmit={handleSubmit}>
              <div className="flex items-center justify-between border-b border-slate-200 p-6 dark:border-slate-700">
                <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50">
                  {editingDirigente ? 'Editar Dirigente' : 'Novo Dirigente'}
                </h2>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="text-2xl leading-none text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  ×
                </button>
              </div>

              <div className="max-h-[65vh] space-y-4 overflow-y-auto p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="col-span-2">
                    <label className={labelClass}>CPF *</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        required
                        value={formatCPF(formData.cpf)}
                        onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                        className={inputClass}
                        maxLength={14}
                      />
                      <button
                        type="button"
                        title="Buscar por CPF"
                        onClick={() => handleCpfLookup()}
                        className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                      >
                        Buscar
                      </button>
                    </div>

                    <label className={labelClass}>Nome completo *</label>
                    <input
                      type="text"
                      required
                      value={formData.nomeCompleto}
                      onChange={(e) => setFormData({ ...formData, nomeCompleto: toUpperText(e.target.value) })}
                      className={inputClass}
                    />
                  </div>

                  {foundDirigente && (
                    <div className="col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/60 dark:bg-emerald-950/30">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Dirigente encontrado</div>
                          <div className="text-sm text-emerald-700 dark:text-emerald-100">{foundDirigente.nomeCompleto} - CPF: {formatCPF(foundDirigente.cpf || '')}</div>
                          <div className="text-sm text-slate-600 dark:text-slate-300">E-mail: {foundDirigente.email} | Telefone: {foundDirigente.telefone}</div>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleConfirmVinculo()} className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Vincular</button>
                          <button type="button" onClick={() => { setFoundDirigente(null); setSuccessMessage(null); }} className={secondaryButtonClass}>Cancelar</button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>RG *</label>
                    <input
                      type="text"
                      required
                      value={formData.rg}
                      onChange={(e) => setFormData({ ...formData, rg: toUpperText(e.target.value) })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Data de nascimento *</label>
                    <input
                      type="date"
                      required
                      value={formData.dataNascimento}
                      onChange={(e) => setFormData({ ...formData, dataNascimento: e.target.value })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Sexo *</label>
                    <select
                      required
                      value={formData.sexo}
                      onChange={(e) => setFormData({ ...formData, sexo: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Selecione</option>
                      {SEXO_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Estado civil *</label>
                    <select
                      required
                      value={formData.estadoCivil}
                      onChange={(e) => setFormData({ ...formData, estadoCivil: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Selecione</option>
                      {ESTADO_CIVIL_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Cargo *</label>
                    <select
                      required
                      value={formData.cargo}
                      onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Selecione</option>
                      {CARGO_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Data início *</label>
                    <input
                      type="date"
                      required
                      value={formData.dataInicioCargo}
                      onChange={(e) => setFormData({ ...formData, dataInicioCargo: e.target.value })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>E-mail *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Telefone *</label>
                    <input
                      type="text"
                      required
                      value={formatPhone(formData.telefone)}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value.replace(/\D/g, '') })}
                      className={inputClass}
                      maxLength={15}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Órgão expedidor *</label>
                    <input
                      type="text"
                      required
                      value={formData.orgaoExpedidor}
                      onChange={(e) => setFormData({ ...formData, orgaoExpedidor: toUpperText(e.target.value) })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>UF órgão *</label>
                    <select
                      required
                      value={formData.ufOrgaoExpedidor}
                      onChange={(e) => setFormData({ ...formData, ufOrgaoExpedidor: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Selecione</option>
                      {UF_OPTIONS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Data expedição *</label>
                    <input
                      type="date"
                      required
                      value={formData.dataExpedicao}
                      onChange={(e) => setFormData({ ...formData, dataExpedicao: e.target.value })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Nacionalidade *</label>
                    <input
                      type="text"
                      required
                      value={formData.nacionalidade}
                      onChange={(e) => setFormData({ ...formData, nacionalidade: toUpperText(e.target.value) })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>CEP *</label>
                    <input
                      type="text"
                      required
                      value={formatCEP(formData.cep)}
                      onChange={(e) => setFormData({ ...formData, cep: e.target.value.replace(/\D/g, '') })}
                      className={inputClass}
                      maxLength={9}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Logradouro *</label>
                    <input
                      type="text"
                      required
                      value={formData.logradouro}
                      onChange={(e) => setFormData({ ...formData, logradouro: toUpperText(e.target.value) })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Número *</label>
                    <input
                      type="text"
                      required
                      value={formData.numero}
                      onChange={(e) => setFormData({ ...formData, numero: toUpperText(e.target.value) })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Bairro *</label>
                    <input
                      type="text"
                      required
                      value={formData.bairro}
                      onChange={(e) => setFormData({ ...formData, bairro: toUpperText(e.target.value) })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Cidade *</label>
                    <input
                      type="text"
                      required
                      value={formData.cidade}
                      onChange={(e) => setFormData({ ...formData, cidade: toUpperText(e.target.value) })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>UF *</label>
                    <select
                      required
                      value={formData.uf}
                      onChange={(e) => setFormData({ ...formData, uf: e.target.value })}
                      className={inputClass}
                    >
                      <option value="">Selecione</option>
                      {UF_OPTIONS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-950/40">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className={secondaryButtonClass}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={primaryButtonClass}
                >
                  {editingDirigente ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Inativação */}
      {showInativarModal && inativandoDirigente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 p-6 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50">Inativar dirigente</h2>
              <button
                type="button"
                onClick={() => setShowInativarModal(false)}
                className="text-2xl leading-none text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <p className="mb-4 text-sm text-slate-700 dark:text-slate-300">
                Inativar dirigente: <strong>{inativandoDirigente.nomeCompleto}</strong>
              </p>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Data de término *</label>
                  <input
                    type="date"
                    required
                    value={inativarData.dataTermino}
                    onChange={(e) => setInativarData({ ...inativarData, dataTermino: e.target.value })}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Motivo da inativação *</label>
                  <textarea
                    required
                    value={inativarData.motivo}
                    onChange={(e) => setInativarData({ ...inativarData, motivo: toUpperText(e.target.value) })}
                    className={inputClass}
                    rows={4}
                    maxLength={500}
                    placeholder="Descreva o motivo da inativação..."
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-950/40">
              <button
                type="button"
                onClick={() => setShowInativarModal(false)}
                className={secondaryButtonClass}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleInativar}
                className={dangerButtonClass}
              >
                Inativar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Documentos Pessoais */}
      {showDocumentosModal && dirigenteDocumentos && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4">
          <div className="my-8 w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 p-6 dark:border-slate-700">
              <div>
                <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50">Documentos pessoais</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  {dirigenteDocumentos.nomeCompleto} - {dirigenteDocumentos.cargo}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseDocumentosModal}
                className="text-2xl leading-none text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-6">
              <DocumentosPessoaisUpload
                dirigenteId={dirigenteDocumentos.pessoaId || dirigenteDocumentos.id!}
                cargo={dirigenteDocumentos.cargo}
                onDocumentosChange={() => {
                  // Opcional: recarregar alguma informação se necessário
                  console.log('Documentos atualizados');
                }}
              />
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-950/40">
              <button
                type="button"
                onClick={handleCloseDocumentosModal}
                className={primaryButtonClass}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mensagem de Sucesso */}
      {successMessage && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
          {successMessage}
        </div>
      )}
    </div>
  );
};

export default DiretoriaPage;

